import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, Container, CircularProgress } from '@mui/material'
import ProgressButton from '../../Utils/ProgressButton'
import { entityTextField, entitySelectField } from '../../Utils/EntityField'
import { AppState } from '../../../reducers/index'
import { upsertProduct } from '../../../actions/product/productUpsert'
import { ProductDetailState } from '../../../reducers/product/productDetail'
import Product from '../../../models/Product'
import { useTranslation } from 'react-i18next'
import { ISTableTitle } from '../../Utils/ISTable'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { fetchProduct } from '../../../actions/product/productDetail'
import { BackButton } from '../../Utils/BackButton'
import { ProductBrandListState } from '../../../reducers/product/productBrandList'
import { ProductSectionListState } from '../../../reducers/product/productSectionList'
import { ProductGroupListState } from '../../../reducers/product/productGroupList'
import { ProductSubgroupListState } from '../../../reducers/product/productSubgroupList'
import { ProductMeasurementListState } from '../../../reducers/product/productMeasurementList'
import Selector from '../../Selector'
import { productStatusTypeToDescription, productStatusTypes } from './Products'
import { ISTextField } from '../../Utils/ISTextField'
import { fetchAllProductBrands } from '../../../actions/product/productBrandList'
import { fetchAllProductSections } from '../../../actions/product/productSectionList'
import { fetchAllProductGroups } from '../../../actions/product/productGroupList'
import { fetchAllProductSubgroups } from '../../../actions/product/productSubgroupList'
import { fetchAllProductMeasurements } from '../../../actions/product/productMeasurementList'
import { fetchAllProductMeasurementTypes } from '../../../actions/product/productMeasurementTypeList'
import { ProductMeasurementTypeListState } from '../../../reducers/product/productMeasurementTypeList'

interface Props {
  product: ProductDetailState
  productBrand: ProductBrandListState
  productSection: ProductSectionListState
  productGroup: ProductGroupListState
  productSubgroup: ProductSubgroupListState
  productMeasurement: ProductMeasurementListState
  productMeasurementType: ProductMeasurementTypeListState
}

const empty: Product = {
  id: null,
  code: '',
  description: '',
  information: '',
  brandId: 0,
  brandName: '',
  sectionId: 0,
  sectionName: '',
  groupId: 0,
  groupName: '',
  subgroupId: 0,
  subgroupName: '',
  measurementId: 0,
  measurementName: '',
  order: 0,
  ownerId: 0,
  ownerName: '',
  status: '',
  updated: '',
  price: 0,
  package: 0,
  money: '',
  discounts: null

}

const ProductUpsert: React.FC<Props> = ({ product, productBrand, productSection, productGroup, productSubgroup, productMeasurement, productMeasurementType }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [productInfo, setProductInfo] = useState<Product>(product.product || empty)
  const [brand, setBrand] = useState(product.product?.brandName || '')
  const [section, setSection] = useState(product.product?.sectionName || '')
  const [group, setGroup] = useState(product.product?.groupName || '')
  const [subgroup, setSubgroup] = useState(product.product?.subgroupName || '')
  const [measurement, setMeasurement] = useState(product.product?.measurementName || '')
  const [measurementType, setMeasurementType] = useState('')
  const { t } = useTranslation('product')
  const dispatch = useDispatch()
  const params = useParams<{ productId: string }>()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (params.productId) {
      dispatch(fetchProduct(params.productId))
    }
  }, [dispatch, params.productId])

  useEffect(() => {
    dispatch(fetchAllProductBrands())
    dispatch(fetchAllProductSections())
    dispatch(fetchAllProductGroups())
    dispatch(fetchAllProductSubgroups())
    dispatch(fetchAllProductMeasurements())
    dispatch(fetchAllProductMeasurementTypes())
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [])

  const change = (newProductInfo: Product) => {
    setProductInfo(newProductInfo)
    setChanged(true)
  }

  const changeProduct = async (event: FormEvent) => {
    event.preventDefault()
    try {
      await dispatch(upsertProduct(productInfo))
      setChanged(false)
      if (location.state)
        (location.state as any).update = !(location.state as any).update
      history.goBack()
    }
    catch {
    }
  }

  const setDataBrand = (description: any): number | null => {
    if (description) {
      setBrand(description)
      return productBrand.productBrands?.find((brand) => brand.description === description)?.id || 0
    }
    return null
  }

  const setDataSection = (description: any): number | null => {
    if (description) {
      setSection(description)
      return productSection.productSections?.find((section) => section.description === description)?.id || 0
    }
    return null
  }

  const setDataGroup = (description: any): number | null => {
    if (description) {
      setGroup(description)
      return productGroup.productGroups?.find((group) => group.description === description)?.id || 0
    }
    return null
  }

  const setDataSubgroup = (description: any): number | null => {
    if (description) {
      setSubgroup(description)
      return productSubgroup.productSubgroups?.find((subgroup) => subgroup.description === description)?.id || 0
    }
    return null
  }

  const setDataMeasurementType = (description: any) => {
    if (description) {
      const prodMeasurement = productMeasurement.productMeasurements?.find((measurement) => measurement.description === description)?.id || 0
      const newMeasurementType = productMeasurement.productMeasurements?.find((measurement) => measurement.id === prodMeasurement)?.typeId
      if (newMeasurementType)
        setMeasurementType(productMeasurementType.productMeasurementTypes?.find((measurementType) => measurementType.id === newMeasurementType)?.description || '')
    }
  }


  const setDataMeasurement = (description: any): number | null => {
    if (description) {
      const prodMeasurement = productMeasurement.productMeasurements?.find((measurement) => measurement.description === description)?.id || 0
      const newMeasurementType = productMeasurement.productMeasurements?.find((measurement) => measurement.id === prodMeasurement)?.typeName
      if (newMeasurementType)
        setMeasurementType(newMeasurementType)
      setMeasurement(description)
      return productMeasurement.productMeasurements?.find((measurement) => measurement.description === description)?.id || 0
    }
    return null
  }

  const updateBrand = (e: any) => {
    const description = e.target.value
    const brandId = setDataBrand(description)
    if (brandId) {
      setProductInfo({ ...productInfo, brandId: brandId })
      setChanged(true)
    }
  }

  const updateSection = (e: any) => {
    const description = e.target.value
    const sectionId = setDataSection(description)
    if (sectionId) {
      setProductInfo({ ...productInfo, sectionId: sectionId })
      setChanged(true)
    }
  }

  const updateGroup = (e: any) => {
    const description = e.target.value
    const groupId = setDataGroup(description)
    if (groupId) {
      setProductInfo({ ...productInfo, groupId: groupId })
      setChanged(true)
    }
  }

  const updateSubgroup = (e: any) => {
    const description = e.target.value
    const subgroupId = setDataSubgroup(description)
    if (subgroupId) {
      setProductInfo({ ...productInfo, subgroupId: subgroupId })
      setChanged(true)
    }
  }

  const updateMeasurement = (e: any) => {
    const description = e.target.value
    const measurementId = setDataMeasurement(description)
    if (measurementId) {
      setProductInfo({ ...productInfo, measurementId: measurementId })
      setChanged(true)
    }
  }

  useEffect(() => {
    if (product.product) {
      setProductInfo(params.productId ? product.product : empty)
      setBrand(params.productId ? product.product?.brandName : '')
      setSection(params.productId ? product.product?.sectionName : '')
      setGroup(params.productId ? product.product?.groupName : '')
      setSubgroup(params.productId ? product.product?.subgroupName : '')
      setMeasurement(params.productId ? product.product?.measurementName : '')
      setDataMeasurementType(product.product?.measurementName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [product.product, params.productId])

  const selectorList = (arrayList: any[]) => {
    const list = arrayList?.filter(element => element.active)
    return list ? [...list.map(element => element.description), ''] : []
  }

  const ProductInfoField = useMemo(() => entityTextField(change), [])

  const ProductInfoSelectField = useMemo(() => entitySelectField(change), [])

  const translateProductStatusTypeDescription = (value: string): string => t(productStatusTypeToDescription(value))

  const loading = () => product.isFetching ||
    productBrand.isFetching ||
    productSection.isFetching ||
    productGroup.isFetching ||
    productSubgroup.isFetching ||
    productMeasurement.isFetching ||
    productMeasurementType.isFetching


  return (
    <Container>
      <Paper style={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title={t('title')} />
        {!loading() &&
          <Paper style={{ padding: "10px" }}>
            <form onSubmit={changeProduct}>
              <Stack display="flex" flexDirection="row" flexGrow={1} >
                <ProductInfoField
                  style={{ width: "200px", marginRight: "10px" }}
                  key="code"
                  variant="outlined"
                  label={t("code")}
                  entity={productInfo}
                  attr="code"
                  size="medium"
                />
                <ProductInfoField
                  //style={{ width: "100%"}}
                  key="description"
                  variant="outlined"
                  label={t("description")}
                  entity={productInfo}
                  attr="description"
                  size="medium"
                />
              </Stack>
              <Stack display="flex" flexDirection="row" flexGrow={1} >
                <ProductInfoField
                  style={{ width: "100%" }}
                  key="information"
                  variant="outlined"
                  label={t("information")}
                  entity={productInfo}
                  attr="information"
                  size="medium"
                />
              </Stack>
              <Stack display="flex" flexDirection="row" flexGrow={1} >
                <ProductInfoField
                  style={{ marginRight: "10px" }}
                  key="order"
                  variant="outlined"
                  label={t("order")}
                  entity={productInfo}
                  attr='order'
                  size="medium"
                />
                <ProductInfoSelectField
                  key="status"
                  label={t("status")}
                  entity={productInfo}
                  attr="status"
                  menuItems={productStatusTypes}
                  textAdapter={translateProductStatusTypeDescription}
                  style={{ width: "100%" }}
                />
                <ProductInfoField
                  style={{ marginRight: "10px" }}
                  key="package"
                  variant="outlined"
                  label={t("package")}
                  entity={productInfo}
                  attr='package'
                  size="medium"
                />
              </Stack>
              <Stack display="flex" flexDirection="row" flexGrow={1} >
                <Selector
                  key="section"
                  label={t("section")}
                  menuItems={selectorList(productSection.productSections!)}
                  onChange={updateSection}
                  value={section}
                  style={{ marginRight: "10px", width: '100%' }}
                  required={true} />
                <Selector
                  key="brand"
                  label={t("brand")}
                  menuItems={selectorList(productBrand.productBrands!)}
                  onChange={updateBrand}
                  value={brand}
                  style={{ width: '100%' }}
                  required={true} />
              </Stack>
              <Stack display="flex" flexDirection="row" flexGrow={1} >
                <Selector
                  key="group"
                  label={t("group")}
                  menuItems={selectorList(productGroup.productGroups!)}
                  onChange={updateGroup}
                  value={group}
                  style={{ marginRight: "10px", width: '100%' }}
                  required={true} />
                <Selector
                  key="subgroup"
                  label={t("subgroup")}
                  menuItems={selectorList(productSubgroup.productSubgroups!)}
                  onChange={updateSubgroup}
                  value={subgroup}
                  style={{ width: '100%' }}
                  required={true} />
              </Stack>
              <Stack display="flex" flexDirection="row" flexGrow={1} >
                <Selector
                  key="measurement"
                  label={t("measurement")}
                  menuItems={selectorList(productMeasurement.productMeasurements!)}
                  onChange={updateMeasurement}
                  value={measurement}
                  style={{ marginRight: "10px", width: '100%' }}
                  required={true} />
                <ISTextField
                  key="measurementType"
                  variant="outlined"
                  label={t("measurementType")}
                  value={measurementType}
                  fullWidth={false}
                  size="medium"
                  style={{ width: '100%' }}
                  disabled
                />
              </Stack>
              <Stack display="flex" flexDirection='row' justifyContent="space-between">
                <BackButton />
                <ProgressButton state={product} disabled={!changed}>
                  {t('save')}
                </ProgressButton>
              </Stack>
            </form>
          </Paper>}
        {loading() &&
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={25} />
          </div>
        }
      </Paper>
    </Container>

  )
}

const mapStateToProps = (state: AppState) => ({
  product: state.productDetail,
  productBrand: state.productBrandList,
  productSection: state.productSectionList,
  productGroup: state.productGroupList,
  productSubgroup: state.productSubgroupList,
  productMeasurement: state.productMeasurementList,
  productMeasurementType: state.productMeasurementTypeList
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(ProductUpsert)
